import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import { CONTACT_EMAIL } from './ContactPage';

interface LegalSection {
  title: string;
  /** Paragraphs; a paragraph starting with "- " is a bullet. */
  body: string[];
}

/**
 * Privacy policy (/privacy), Terms of use (/terms) and the Accessibility statement
 * (/accessibility), plan 16. Texts in i18n `privacy.*`, `terms.*`, `accessibility.*`.
 */
export default function LegalPage({ kind }: { kind: 'privacy' | 'terms' | 'accessibility' }) {
  const { t } = useTranslation();
  usePageTitle(t(`${kind}.title`));
  const sections = t(`${kind}.sections`, { returnObjects: true }) as LegalSection[];

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
      <div className="space-y-1">
        <h1 className="text-lg font-semibold text-ink">{t(`${kind}.title`)}</h1>
        <p className="text-xs text-ash">
          {t('legal.lastUpdated', { date: t(kind === 'accessibility' ? 'accessibility.date' : 'legal.date') })}
        </p>
        <p className="text-sm text-ink">{t(`${kind}.intro`)}</p>
      </div>
      {Array.isArray(sections) &&
        sections.map((section) => (
          <section key={section.title} className="space-y-2">
            <h2 className="text-sm font-semibold text-ink">{section.title}</h2>
            <LegalBody paragraphs={section.body} />
          </section>
        ))}
      <p className="text-sm text-ink">
        {t('legal.questions')}{' '}
        <Link to="/contact" className="underline">
          {t('contact.title')}
        </Link>
        {' · '}
        <a href={`mailto:${CONTACT_EMAIL}`} className="underline" dir="ltr">
          {CONTACT_EMAIL}
        </a>
      </p>
      <p className="flex flex-wrap gap-x-3 text-xs text-ash">
        {(['privacy', 'terms', 'accessibility'] as const)
          .filter((k) => k !== kind)
          .map((k) => (
            <Link key={k} to={`/${k}`} className="underline">
              {t(`${k}.title`)}
            </Link>
          ))}
      </p>
    </div>
  );
}

function LegalBody({ paragraphs }: { paragraphs: string[] }) {
  const out: React.ReactNode[] = [];
  let bullets: string[] = [];
  const flush = () => {
    if (bullets.length) {
      out.push(
        <ul key={`ul${out.length}`} className="list-disc ps-5 space-y-1 text-sm text-ink">
          {bullets.map((b) => (
            <li key={b}>{b}</li>
          ))}
        </ul>
      );
      bullets = [];
    }
  };
  for (const p of paragraphs ?? []) {
    if (p.startsWith('- ')) {
      bullets.push(p.slice(2));
    } else {
      flush();
      out.push(
        <p key={`p${out.length}`} className="text-sm text-ink">
          {p}
        </p>
      );
    }
  }
  flush();
  return <>{out}</>;
}
